import { ExternalLink } from "lucide-react";
import { useGetOne } from "ra-core";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { ContactConversationTimeline } from "../contacts/ContactConversationTimeline";
import { ContactEchoesPanel } from "../echoflicks/EchoList";
import {
  formatRevenue,
  type ExperimentAssignment,
  type ExperimentJourneyEvent,
} from "../experiments/experimentClient";
import type { Contact } from "../types";
import {
  formatDateTime,
  journeyEventDetail,
  journeyEventLabel,
  outcomeLabel,
  stageLabel,
} from "./monetizationModel";

const MILESTONES: {
  label: string;
  field: keyof ExperimentAssignment;
}[] = [
  { label: "Assigned", field: "assigned_at" },
  { label: "Echo started", field: "last_echo_started_at" },
  { label: "Rated", field: "last_rating_at" },
  { label: "Offer sent", field: "last_offer_prompted_at" },
  { label: "Package selected", field: "last_offer_selected_at" },
  { label: "Checkout", field: "last_checkout_created_at" },
  { label: "Paid", field: "last_payment_at" },
  { label: "Refunded", field: "last_refund_at" },
];

export function MonetizationContactQuickViewModal({
  assignment,
  onClose,
}: {
  assignment: ExperimentAssignment | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={assignment != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
        {assignment ? (
          <ModalBody assignment={assignment} onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ModalBody({
  assignment,
  onClose,
}: {
  assignment: ExperimentAssignment;
  onClose: () => void;
}) {
  const fallback = asContact(assignment);
  const { data } = useGetOne<Contact>(
    "contacts",
    { id: assignment.user_id },
    { enabled: Boolean(assignment.user_id), retry: false },
  );
  const contact = data ?? fallback;
  const displayName = contactName(contact, assignment.user_id);
  const outcome = assignment.conversion_outcome;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{displayName}</DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="tabular-nums">{assignment.user_id}</span>
            <Badge variant="outline" className="font-normal capitalize">
              {assignment.acquisition_surface ?? "Unknown source"}
            </Badge>
            <Badge variant={outcome === "paid" ? "default" : "secondary"}>
              {outcomeLabel(outcome)}
            </Badge>
            <span>
              Furthest stage:{" "}
              <strong>{stageLabel(assignment.furthest_stage)}</strong>
            </span>
          </div>
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {MILESTONES.map((milestone) => {
          const value = assignment[milestone.field];
          const timestamp = typeof value === "string" ? value : null;
          return (
            <div
              key={milestone.label}
              className={`rounded-md border p-2 ${timestamp ? "" : "opacity-50"}`}
            >
              <div className="text-[11px] text-muted-foreground">
                {milestone.label}
              </div>
              <div className="text-xs tabular-nums">
                {formatDateTime(timestamp)}
              </div>
            </div>
          );
        })}
      </div>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Monetization journey</h3>
          <span className="text-xs text-muted-foreground">
            {assignment.post_offer_event_count ?? 0} tracked action(s) after the
            offer
          </span>
        </div>
        <JourneyTimeline events={assignment.journey_events ?? []} />
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">
            Echoes &amp; internal balance
          </h3>
          <ContactCreditSummary contact={contact} assignment={assignment} />
        </div>
        <ContactEchoesPanel contact={contact} />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold">WhatsApp conversation</h3>
        <ContactConversationTimeline
          contact={contact}
          heightClassName="h-[460px]"
        />
      </section>

      <div className="flex justify-end">
        <Button asChild variant="outline" size="sm" onClick={onClose}>
          <Link to={`/contacts/${encodeURIComponent(String(contact.id))}/show`}>
            Open full profile
            <ExternalLink className="size-4" />
          </Link>
        </Button>
      </div>
    </>
  );
}

function JourneyTimeline({ events }: { events: ExperimentJourneyEvent[] }) {
  if (!events.length) {
    return (
      <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        No durable experiment events were found for this assignment.
      </div>
    );
  }

  return (
    <ol className="divide-y rounded-md border">
      {events.map((event) => {
        const detail = journeyEventDetail(event);
        const amount = eventAmount(event);
        return (
          <li
            key={event.event_id}
            className="flex flex-wrap items-start justify-between gap-2 px-3 py-2"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {journeyEventLabel(event.event_type)}
              </p>
              {detail || amount ? (
                <p className="text-xs text-muted-foreground">
                  {[detail, amount].filter(Boolean).join(" · ")}
                </p>
              ) : null}
            </div>
            <time className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {formatDateTime(event.occurred_at)}
            </time>
          </li>
        );
      })}
    </ol>
  );
}

function eventAmount(event: ExperimentJourneyEvent): string | null {
  const amount =
    event.event_type === "commerce.payment_refunded" ||
    event.event_type === "commerce.payment_partially_refunded"
      ? event.refunded_amount_minor
      : event.amount_minor;
  if (amount == null || !event.currency) return null;
  return formatRevenue(amount, event.currency);
}

function asContact(assignment: ExperimentAssignment): Contact {
  return {
    id: assignment.user_id,
    first_name: "WhatsApp",
    last_name: "",
    whatsapp_user_id: assignment.user_id,
    phone_e164: assignment.user_id,
  } as unknown as Contact;
}

function contactName(contact: Contact, fallback: string): string {
  const name = [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name && name.toLowerCase() !== "whatsapp" ? name : fallback;
}

function ContactCreditSummary({
  contact,
  assignment,
}: {
  contact: Contact;
  assignment: ExperimentAssignment;
}) {
  const credits =
    typeof contact.credit_balance === "number" ? contact.credit_balance : null;
  const echoes =
    typeof contact.video_count === "number" ? contact.video_count : null;
  const paidOrders = assignment.metrics?.paid_orders ?? 0;
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <span className="rounded-md border px-2 py-1">
        <span className="text-muted-foreground">Paid orders </span>
        <strong className="tabular-nums">{paidOrders}</strong>
      </span>
      <span className="rounded-md border px-2 py-1">
        <span className="text-muted-foreground">Net revenue </span>
        <strong className="tabular-nums">
          {paidOrders
            ? formatRevenue(
                assignment.metrics.net_revenue_minor,
                assignment.metrics.currency,
              )
            : "—"}
        </strong>
      </span>
      <span className="rounded-md border px-2 py-1">
        <span className="text-muted-foreground">Internal credits </span>
        <strong className="tabular-nums">{credits ?? "—"}</strong>
      </span>
      <span className="rounded-md border px-2 py-1">
        <span className="text-muted-foreground">Echoes made </span>
        <strong className="tabular-nums">{echoes ?? "—"}</strong>
      </span>
    </div>
  );
}
